import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import Header from '@/common/header';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';

import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { SiteFooter } from './components/site-footer';
import { TrialDialogContext } from './components/trial-cta';
import { TrialDialog } from './components/trial-dialog';
import { V4A_NAMESPACE } from './constants';
import { localeTag } from './engines/geo';
import { useCounters } from './hooks/use-counters';
import { useScrollReveal } from './hooks/use-page-motion';
import multiInternetStyles from './multi-internet.module.css';
import multiconexaoStyles from './multiconexao.module.css';
import pageStyles from './page.module.css';
import { BeforeAfter } from './sections/before-after';
import { Champions } from './sections/champions';
import { Faq } from './sections/faq';
import { FinalCta } from './sections/final-cta';
import { Games } from './sections/games';
import { Grimoire } from './sections/grimoire';
import { Hero } from './sections/hero';
import { NetworkMap } from './sections/map';
import { MultiInternet } from './sections/multi-internet';
import { Multiconexao } from './sections/multiconexao';
import { Potions } from './sections/potions';
import shellStyles from './v4a.module.css';

interface ArenaProps extends DefaultTrialProps {}

type ArenaPageProps = Pick<DefaultTrialProps, 'trialConfigId' | 'trialPageId' | 'isDisabledButton'>;

function canOpenDialog(): boolean {
  return typeof window !== 'undefined' && typeof window.HTMLDialogElement === 'function' && typeof window.HTMLDialogElement.prototype.showModal === 'function';
}

/** One locale's page: remounted on a language change, so every imperative island restarts clean. */
function ArenaPage({ trialConfigId, trialPageId, isDisabledButton = false }: ArenaPageProps) {
  const { t, i18n } = useTranslation(V4A_NAMESPACE);
  const rootRef = useRef<HTMLDivElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  useScrollReveal(rootRef);
  useCounters(rootRef, localeTag(i18n.language));

  // Every "Testar grátis" opens the enrolment dialog; without <dialog> support the link lands on the card.
  const openTrial = useCallback(() => {
    if (!canOpenDialog()) return false;
    setDialogOpen(true);
    return true;
  }, []);
  const closeTrial = useCallback(() => setDialogOpen(false), []);
  const dialogContext = useMemo(() => ({ openTrial }), [openTrial]);
  const formProps = { trialConfigId, trialPageId, isDisabledButton };

  return (
    <TrialDialogContext.Provider value={dialogContext}>
      <Header variant='primary' />
      <div ref={rootRef} className={classNames(shellStyles.root, pageStyles.root, multiconexaoStyles.root, multiInternetStyles.root)} style={{ '--v4a-font': bai_jamjuree.style.fontFamily } as CSSProperties}>
        <a className='skip-link fixed start-3 top-3 z-[var(--z-top)] rounded-[var(--r-sm)] bg-[color:var(--accent)] px-4 py-3 font-bold' href='#conteudo'>
          {t('skip_link')}
        </a>
        <main id='conteudo'>
          <Hero {...formProps} />
          <Grimoire />
          <BeforeAfter />
          <Multiconexao />
          <MultiInternet />
          <NetworkMap />
          <Potions />
          <Champions />
          <Games />
          <Faq />
          <FinalCta {...formProps} />
        </main>
        <SiteFooter />
        <TrialDialog isOpen={dialogOpen} onClose={closeTrial} {...formProps} />
      </div>
    </TrialDialogContext.Provider>
  );
}

/**
 * v4a, "Sua internet está amaldiçoada. O NoPing é o contra-feitiço." Trial LP in the design's own shell:
 * two enrolment cards (hero and closing band) plus a dialog every trial CTA opens, all three running the
 * same trial signup (`custom` = trial config id, `trialPageId`, UTM and click id).
 */
export function Arena({ trialConfigId, trialPageId, isDisabledButton = false }: ArenaProps) {
  const { i18n } = useTranslation(V4A_NAMESPACE);
  return <ArenaPage key={i18n.language} trialConfigId={trialConfigId} trialPageId={trialPageId} isDisabledButton={isDisabledButton} />;
}
